import type { Traducao } from '../tipos';

/** Textos de `cronograma`. Preenchido pela tradução da área — ver ../README.md. */
export const cronograma: Traducao = {
  pt: {
    titulo: 'Cronograma Operacional',
    subtitulo: 'Gestão interna do cronograma com edição de tarefas, vínculo e histórico de revisões.',
    resumo: {
      projeto: 'Projeto:',
      totalTarefas: 'Total de tarefas:',
      atualizacao: 'Atualização:',
    },
    acao: {
      sincronizar: 'Sincronizar Máquinas',
      sincronizarTitulo: 'Criar máquinas no dashboard a partir das tarefas sem vínculo',
      salvarAjustes: 'Salvar ajustes',
      atualizarRevisoes: 'Atualizar revisões',
    },
    filtro: {
      todasSemanas: 'Todas semanas',
      todasAreas: 'Todas áreas',
    },
    lista: {
      titulo: 'Cronograma — Nível 3',
      levantamentos: 'Levantamentos ({{total}})',
      revisoes: 'Revisões ({{total}})',
      semCronograma: 'Cronograma não disponível para este projeto. O sistema tenta carregar automaticamente da planilha base "Curva S.xlsx".',
    },
    tabela: {
      colunaTarefa: 'Tarefa',
      colunaInicio: 'Início',
      colunaTermino: 'Término',
      colunaVinculo: 'Vínculo',
      colunaPercentual: '% Efetivo',
      vazio: 'Nenhuma tarefa para os filtros atuais.',
    },
    // Rótulo do badge: o valor cru do enum é o que já aparecia na tela em
    // português, então `pt` o preserva e só `en` ganha texto legível.
    statusExecucao: {
      concluida: 'concluida',
      atrasada: 'atrasada',
      pendente_vinculo: 'pendente_vinculo',
      em_andamento: 'em_andamento',
      pendente: 'pendente',
    },
    statusVinculo: {
      vinculado: 'vinculado',
      pendente: 'pendente',
      ambiguo: 'ambiguo',
    },
    formato: {
      percentual: '{{valor}}%',
    },
    estado: {
      semProjeto: 'Selecione um projeto para gerenciar o cronograma.',
      carregando: 'Carregando cronograma...',
    },
    mensagem: {
      semConexao: 'Sem conexão com servidor. Exibindo cronograma em cache local.',
      erroCarregar: 'Não foi possível carregar o cronograma.',
      erroCarregarGenerico: 'Erro ao carregar cronograma.',
      falhaAtualizar: 'Falha ao atualizar tarefa.',
      erroSalvar: 'Erro ao salvar edição da tarefa.',
      nenhumaMaquinaPendente: 'Nenhuma máquina pendente encontrada.',
      sincronizacaoConcluida: '{{ativos}} máquina(s) criada(s) e {{tarefas}} tarefa(s) vinculada(s).',
      erroSincronizar: 'Erro ao sincronizar.',
      erroSincronizarMaquinas: 'Erro ao sincronizar máquinas.',
    },
    edicao: {
      titulo: 'Editar tarefa do cronograma',
      nomeTarefa: 'Nome da tarefa',
      sismanaAntigo: 'SISMANA antigo',
      mes: 'Mês',
      inicio: 'Início',
      termino: 'Término',
      duracaoDias: 'Duração (dias)',
      percentualOverride: '% override',
      ativoId: 'Ativo ID (vínculo manual)',
    },
    revisoes: {
      titulo: 'Histórico de revisões',
      colunaUsuario: 'Usuário',
      colunaCampo: 'Campo',
      colunaValorAnterior: 'Valor anterior',
      colunaNovoValor: 'Novo valor',
      vazio: 'Sem revisões registradas para esta tarefa.',
      sistema: 'Sistema',
    },
  },
  en: {
    titulo: 'Operational Schedule',
    subtitulo: 'Internal schedule management with task editing, linking and revision history.',
    resumo: {
      projeto: 'Project:',
      totalTarefas: 'Total tasks:',
      atualizacao: 'Updated:',
    },
    acao: {
      sincronizar: 'Sync Machines',
      sincronizarTitulo: 'Create machines in the dashboard from the unlinked tasks',
      salvarAjustes: 'Save changes',
      atualizarRevisoes: 'Refresh revisions',
    },
    filtro: {
      todasSemanas: 'All weeks',
      todasAreas: 'All areas',
    },
    lista: {
      titulo: 'Schedule — Level 3',
      levantamentos: 'Surveys ({{total}})',
      revisoes: 'Revisions ({{total}})',
      semCronograma: 'Schedule not available for this project. The system tries to load it automatically from the base spreadsheet "Curva S.xlsx".',
    },
    tabela: {
      colunaTarefa: 'Task',
      colunaInicio: 'Start',
      colunaTermino: 'Finish',
      colunaVinculo: 'Link',
      colunaPercentual: '% Effective',
      vazio: 'No task matches the current filters.',
    },
    statusExecucao: {
      concluida: 'completed',
      atrasada: 'overdue',
      pendente_vinculo: 'link pending',
      em_andamento: 'in progress',
      pendente: 'pending',
    },
    statusVinculo: {
      vinculado: 'linked',
      pendente: 'pending',
      ambiguo: 'ambiguous',
    },
    formato: {
      percentual: '{{valor}}%',
    },
    estado: {
      semProjeto: 'Select a project to manage the schedule.',
      carregando: 'Loading schedule...',
    },
    mensagem: {
      semConexao: 'No connection to the server. Showing the schedule from the local cache.',
      erroCarregar: 'Could not load the schedule.',
      erroCarregarGenerico: 'Error loading the schedule.',
      falhaAtualizar: 'Failed to update the task.',
      erroSalvar: 'Error saving the task changes.',
      nenhumaMaquinaPendente: 'No pending machine found.',
      sincronizacaoConcluida: '{{ativos}} machine(s) created and {{tarefas}} task(s) linked.',
      erroSincronizar: 'Error while syncing.',
      erroSincronizarMaquinas: 'Error while syncing machines.',
    },
    edicao: {
      titulo: 'Edit schedule task',
      nomeTarefa: 'Task name',
      sismanaAntigo: 'Old SISMANA',
      mes: 'Month',
      inicio: 'Start',
      termino: 'Finish',
      duracaoDias: 'Duration (days)',
      percentualOverride: '% override',
      ativoId: 'Asset ID (manual link)',
    },
    revisoes: {
      titulo: 'Revision history',
      colunaUsuario: 'User',
      colunaCampo: 'Field',
      colunaValorAnterior: 'Previous value',
      colunaNovoValor: 'New value',
      vazio: 'No revisions recorded for this task.',
      sistema: 'System',
    },
  },
};
